import React, { useEffect, useState, useRef } from "react";
import MyEditor from "./AddNewTaskModalEditor";
import { FIREBASE_CONVERSATIONS } from "../Utils/constant";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  onSnapshot,
  orderBy,
  addDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../Utils/firebase";
import { getProfile } from "../actions/login";
import { api } from "../actions/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ShareDocumentModal = ({ show, title, file, content, onClose }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState("");
  const [messageContent, setMessageContent] = useState(content || "");
  const [isSharing, setIsSharing] = useState(false); // New state for loading indicator

  useEffect(() => {
    setMessageContent(content || "");
  }, [content]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await getProfile();
        setUserProfile(profile);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (!userProfile?.id) return;

    const q = query(
      collection(db, FIREBASE_CONVERSATIONS),
      where("participants", "array-contains", String(userProfile.id)),
      orderBy("lastMessageTime", "desc"),
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const userConversations = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.visibleTo?.includes(String(userProfile.id))) {
          userConversations.push({ id: doc.id, ...data });
        }
      });
      setConversations(userConversations);
    });

    return () => unsubscribe();
  }, [userProfile?.id]);

  const handleChatChange = (event) => {
    setSelectedChat(event.target.value);
  };

  const getChatName = (conversation) => {
    if (conversation.type === "group") {
      return "Squad: " + conversation.metadata?.groupName || "Unnamed Group";
    } else {
      const otherParticipantId = conversation.participants.find(
        (id) => id !== String(userProfile.id),
      );
      return (
        "Direct Message: " +
          conversation.participantDetails[otherParticipantId]?.name ||
        "Unnamed Chat"
      );
    }
  };

  const handleShare = async () => {
    const trimmedContent = messageContent.replace(/<[^>]*>/g, "").trim();

    if (!selectedChat) {
      toast.error("Please select a chat.");
      return;
    }

    if (!trimmedContent && !file) {
      toast.error("Please write a message or ensure a file is attached.");
      return;
    }

    setIsSharing(true); // Start sharing process

    try {
      await handleSendMessage(selectedChat, messageContent, title, file);
      toast.success("Event has been shared");
      onClose();
    } catch (error) {
      toast.error("Failed to share document. Please try again.");
    } finally {
      setIsSharing(false); // End sharing process regardless of success or failure
    }
  };

  const handleSendMessage = async (
    conversationId,
    content,
    title,
    file = null,
  ) => {
    let fileData = null;

    if (file) {
      fileData = await handleSendFile(file);
    }

    try {
      const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
      const convoSnap = await getDoc(convoRef);

      if (!convoSnap.exists()) {
        console.error("Conversation not found");
        throw new Error("Conversation not found");
      }
      const convoData = convoSnap.data();
      const visibleToUserIds = convoData.participants || [];

      const messageData = {
        type: "text",
        metadata: {
          title: title,
        },
        reactions: [],
        isRead: null,
        senderId: userProfile.id,
        senderName: userProfile.full_name,
        senderProfileImage: userProfile.image,
        timestamp: serverTimestamp(),
        content: content,
        isFile: !!fileData,
        visibleTo: visibleToUserIds,
      };

      if (fileData) {
        messageData.file = {
          url: fileData.media_url || null,
          type: fileData.type || null,
        };
        messageData.type = "file";
      }

      await addDoc(
        collection(db, FIREBASE_CONVERSATIONS, conversationId, "messages"),
        messageData,
      );

      await runTransaction(db, async (transaction) => {
        const convoSnap = await transaction.get(convoRef);
        if (!convoSnap.exists()) return;

        const convoData = convoSnap.data();
        const currentUnread = convoData.unreadCount || {};
        // Deduplicate and sync to repair historical metadata corruption
        const allParticipants = Array.from(
          new Set([
            ...(convoData.visibleTo || []),
            ...(convoData.participants || []),
          ]),
        ).filter(Boolean);

        const updatedUnread = { ...currentUnread };

        // Ensure every participant is in updatedUnread and increment if not sender
        allParticipants.forEach((uid) => {
          const uidStr = String(uid);
          if (!(uidStr in updatedUnread)) {
            updatedUnread[uidStr] = 0;
          }

          if (uidStr !== String(userProfile.id)) {
            updatedUnread[uidStr] = (updatedUnread[uidStr] || 0) + 1;
          }
        });

        // Derive unreadUserIds explicitly
        const unreadUserIds = Object.keys(updatedUnread).filter(
          (uid) => updatedUnread[uid] > 0,
        );

        transaction.update(convoRef, {
          lastMessage: content || title || "File shared",
          lastMessageSenderId: userProfile.id,
          lastMessageTime: serverTimestamp(),
          updatedAt: serverTimestamp(),
          unreadCount: updatedUnread,
          unreadUserIds,
          participants: allParticipants, // Repair the list
        });
      });
    } catch (error) {
      console.error("Failed to send message:", error);
      throw error; // Propagate the error to the calling function
    }
  };

  const handleSendFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append("media", file);

      const response = await api.post("/user/chat/media/upload", formData);

      if (response.status !== 200) {
        throw new Error("Upload failed");
      }

      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const handlePreview = () => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, "_blank");
    }
  };

  const handleDownload = () => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    }
  };

  return (
    <div className="modal fade show d-block" id="shareScoreModal" tabIndex={-1}>
      <div className="modal-dialog habit-tracker-modal modal-dialog-centered">
        <div className="modal-content habit-modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="shareScoreModalLabel">
              {title}
            </h5>
            <button
              type="button"
              className="btn-close custom-white-close"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>
          <hr />
          <div className="modal-body">
            <div className="mb-3">
              <div
                className="chat-container"
                style={{ padding: "0px", height: "auto", overflowY: "unset" }}
              >
                <div className="textfield-1 habits-admin-modal">
                  <select
                    className="text-body-b2 w-100"
                    style={{ width: "430px", borderColor: "#364756" }}
                    value={selectedChat}
                    onChange={handleChatChange}
                  >
                    <option disabled hidden value="">
                      Select Chat
                    </option>
                    {conversations.map((conversation) => (
                      <option key={conversation.id} value={conversation.id}>
                        {getChatName(conversation)}
                      </option>
                    ))}
                  </select>
                  <label
                    className="text-body-b2"
                    style={{ backgroundColor: "#132534" }}
                  >
                    Chat
                  </label>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="notes-text-editor">
                <label className="text-small-s2">Description</label>
                <MyEditor
                  value={messageContent}
                  onChange={setMessageContent}
                  readOnly={isSharing}
                />
              </div>
            </div>

            {file && (
              <div className="mb-4">
                <p className="text-small-s2 mb-1">Attached File</p>
                <p className="text-body-s2">{file.name}</p>
                <div className="d-flex mt-2">
                  <button
                    className="btn btn-secondary me-2"
                    onClick={handlePreview}
                    disabled={isSharing}
                  >
                    Preview PDF
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleDownload}
                    disabled={isSharing}
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              className="btn-2 btn-1-primary habit-share-btn"
              onClick={handleShare}
              disabled={isSharing} // Disable the button when sharing
            >
              {isSharing ? (
                <>
                  <span className="ms-2">Sharing...</span>
                </>
              ) : (
                "Share"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareDocumentModal;
